<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header v-if="!isInitialLoad" :heading="pageHeading"></common-page-header>
                <switch-input
                    :label="$t('pages.referrals.new.steps.results.view_map')"
                    :value="showMap"
                    :isLabelOnLeft="true"
                    @change="showMap = !showMap"
                ></switch-input>
            </common-content-spacer>
            <common-loader v-if="isInitialLoad" :isProcessing="isInitialLoad"></common-loader>
            <div v-else class="members-wrapper" :class="{ 'members-wrapper-map': showMap }">
                <common-content-spacer v-if="results.length" class="members-container" height="medium">
                    <common-content-spacer
                        height="medium"
                        class="members-container--grid"
                        :class="{ 'members-container--grid__active': selectedPracticeId, 'blurred-grid': isProcessing }"
                    >
                        <common-grid>
                            <common-grid-column
                                v-for="(result, index) in results"
                                :id="result?.id"
                                :key="index"
                                :class="{
                                    'members-container--grid__active--practice': selectedPracticeId == result?.id,
                                }"
                            >
                                <practice-member-card
                                    v-if="result.userRole"
                                    :id="result.id"
                                    :physician="result"
                                    @checkAvailability="checkAvailability"
                                    @added-to-network="memberAddedToNetwork"
                                ></practice-member-card>

                                <ancillary-member-card
                                    v-if="!result.userRole"
                                    :id="result?.id"
                                    :ancillary="result"
                                    @checkAvailability="checkAncillaryAvailability($event)"
                                ></ancillary-member-card>
                            </common-grid-column>
                        </common-grid>
                    </common-content-spacer>
                    <common-buttons>
                        <common-button
                            v-if="totalCount > results.length"
                            :label="$t('pages.referrals.index.buttons.show_more')"
                            :isProcessing="isProcessing"
                            type="primary"
                            size="large"
                            @click="showMore()"
                        ></common-button>
                    </common-buttons>
                </common-content-spacer>
                <div v-if="showMap" class="map-container">
                    <map-component
                        :markers="markers"
                        :preselectedMilePrefill="selectedDistance"
                        @distance-changed="distanceChanged"
                        @popup-open="onPopupOpen"
                        @popup-close="selectedPracticeId = null"
                    ></map-component>
                </div>
            </div>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import UsersAPI from 'api/UsersAPI'
    import AppLayout from 'components/layouts/AppLayout'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import AncillaryMemberCard from 'components/page-elements/practice/AncillaryMemberCard'
    import MapComponent from 'components/page-elements/map/MapComponent'
    import SwitchInput from 'components/form-inputs/SwitchInput'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin],

        components: {
            AppLayout,
            PracticeMemberCard,
            AncillaryMemberCard,
            MapComponent,
            SwitchInput,
        },

        data() {
            return {
                results: [],
                isInitialLoad: false,
                isProcessing: false,
                offset: 0,
                totalCount: 0,
                showMap: false,
                markers: [],
                selectedPracticeId: null,
                selectedDistance: 20,
            }
        },

        computed: {
            pageHeading() {
                return this.$tc(`pages.index.members_nearby.heading`, this.totalCount, {
                    count: this.totalCount,
                })
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.index.members_nearby.breadcrumbs.members'),
                    },
                ]
            },
        },

        methods: {
            loadMatchesWithDifferentRange(range) {
                this.getMembersNearby({ ...this.$route.query, range, offset: this.offset })
            },

            checkAvailability(query) {
                const q = { ...this.$route.query, ...query }
                if (!q.subSpecialityId) {
                    const physicianSpecialities = query.target.specialities
                    q.subSpecialityId = physicianSpecialities.find((specObj) => {
                        return Number(specObj.speciality.id) === Number(q.specialityId)
                    }).subSpeciality.id
                }
                this.$router.push({
                    name: 'referrals.new.make',
                    query: q,
                    params: {
                        existingReferral: this.$route.params.existingReferral,
                        originalQuery: this.$route.query,
                    },
                })
            },

            memberAddedToNetwork({ userId }) {
                const user = this.results.find((user) => user.id === userId)
                user.isInMyNetwork = true
            },

            distanceChanged(distance) {
                this.selectedDistance = distance
                this.getMembersNearby({ ...this.$route.query, distance, offset: this.offset }, false)
            },

            getMembersNearby(data, append) {
                this.isProcessing = true
                UsersAPI.getMembersNearby(this.$auth.getActiveMember().id, data).then((results) => {
                    this.isProcessing = false
                    this.isInitialLoad = false
                    if (append) {
                        const container = document.querySelector('.members-container--grid')
                        const previousScrollHeight = container ? container.scrollHeight : 0
                        this.results = this.results.concat(results)

                        this.getLocationsMarkers(this.results)
                        this.$nextTick(() => {
                            const newScrollHeight = container.scrollHeight
                            const scrollDifference = newScrollHeight - previousScrollHeight
                            if (scrollDifference > 0) {
                                container.scrollBy({
                                    top: scrollDifference / 4,
                                    behavior: 'smooth',
                                })
                            }
                        })
                        return
                    }
                    this.results = results
                    this.getLocationsMarkers(this.results)
                })
            },

            getLocationsMarkers(results) {
                const locations = []
                this.markers = []

                results.forEach((item) => {
                    item.practice?.locations.forEach((location) => {
                        const locationWithPracticeName = { ...location, item }
                        locations.push(locationWithPracticeName)
                    })
                })
                this.markers = locations
            },

            showMore() {
                this.offset = Number(this.$route.query.limit) + this.offset
                this.getMembersNearby({ ...this.$route.query, offset: this.offset }, true)
            },

            onPopupOpen(event) {
                this.selectedPracticeId = event
                const el = document.getElementById(this.selectedPracticeId)
                el.scrollIntoView({
                    behavior: 'auto',
                    block: 'center',
                    inline: 'center',
                })
            },
        },

        watch: {
            results() {
                if (this.results._headers) {
                    this.totalCount = this.results._headers['x-total-count']
                }
            },
        },

        mounted() {
            this.isInitialLoad = true
            this.getMembersNearby({}, false)
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .members-wrapper {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        @include breakpoint(laptop) {
            flex-direction: column-reverse;
        }
        .map-container {
            height: 600px;
        }

        .members-container {
            display: flex;
            flex-wrap: wrap;
            width: 100%;

            &--grid {
                max-height: 60vh;
                overflow-y: scroll;
                overflow-x: hidden;
                width: 100%;

                &__active {
                    .card {
                        opacity: 0.5;
                    }

                    &--practice {
                        .card {
                            opacity: 1;
                        }
                    }
                }
            }

            .buttons {
                width: 100%;
            }
        }
    }

    .members-wrapper-map {
        .map-container {
            @include breakpoint(laptop) {
                width: 100%;
                max-height: 600px;
                height: 600px;
                margin-bottom: 70px;
            }

            .map-component {
                @include breakpoint(laptop) {
                    width: 100%;
                    max-height: 600px;
                    height: 600px;
                }
            }
            width: 450px;
        }

        .members-container {
            .grid__column {
                @include breakpoint(tablet) {
                    flex-basis: 100%;
                }
                flex-basis: calc(100% / 2);
            }
            @include breakpoint(laptop) {
                width: 100%;
            }
            width: calc(100% - 550px);
        }
    }
    .blurred-grid {
        position: relative;
        filter: blur(1px);
        pointer-events: none;
        opacity: 0.6;
        transition: all 0.3s ease-in-out;

        &::after {
            content: '';
            position: absolute;
            inset: 0;
            background: rgba(255, 255, 255, 0.3);
        }
    }
</style>
